// Angular
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// RXJS
import { filter, tap } from 'rxjs/operators';

// Afidium
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'libraries/component/dialogs/confirm-dialog/confirm-dialog.component';
import { INotification, NotificationType } from 'libraries/interfaces/INotification';
import { ILoader } from 'libraries/component/loader/ILoader';
import { BookingsListComponent } from "app_sasha/pages/booking-search-result/components/bookings-list/bookings-list.component";
import { IGetBookingsCommonResponse } from 'libraries/service/resources/booking/interfaces/IGetBookings';

interface IBookingList {
    label: string;
    bookings: IGetBookingsCommonResponse[];
}

export interface IProcessingReassuranceLabels {
    processableBookingsIsEmpty: string;
    processableBooking: string;
    processableBookings: string;

    notProcessableBookingsIsEmpty: string;
    notProcessableBooking: string;
    notProcessableBookings: string;

    processBookings: string;

    notProcessableReasons: {
        main: string
        reasons: string[]
    };

    dialog: {
        title: string;
        question: string;
        cancel: string;
        accept: string;
    };

    loaderMessage: string;
}

/**
 * Reusable component for reassurance before mass processing.
 */
@Component({
    selector: 'processing-reassurance',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>

            <mat-tab-group [disableRipple]="true">
                <mat-tab [label]="bookingListProcessable.label"
                         *ngIf="bookingListProcessable.bookings && bookingListProcessable.bookings.length">
                    <notification *ngIf="notification.active" class="notification" [type]="notification.type">
                        {{ notification.message }}
                    </notification>

                    <bookings-list #bookingsList
                                   *ngIf="!loader.active"
                                   [recordset]="{
                                            total: bookingListProcessable.bookings.length,
                                            first: 1,
                                            nbrecord: bookingListProcessable.bookings.length
                                        }"
                                   [canFilter]="false"
                                   [canExportExcel]="false"
                                   [selectedOptions]="bookingListProcessable.bookings"
                                   [bookings]="bookingListProcessable.bookings">

                        <ng-container class="list-header">
                            <btn *ngIf="bookingListProcessable.bookings.length > 0 && bookingsList.selectedOptions.length"
                                 class="e2e-processBookings"
                                 type="actionDo"
                                 [label]="wordings.processBookings"
                                 (click)="launchAction(bookingsList.selectedOptions)"></btn>
                        </ng-container>

                    </bookings-list>

                    <loader *ngIf="loader.active" [message]="loader.message"></loader>
                </mat-tab>

                <mat-tab [label]="bookingListNotProcessable.label"
                         *ngIf="bookingListNotProcessable.bookings && bookingListNotProcessable.bookings.length">
                    <notification class="notification" type="info">
                        {{ wordings.notProcessableReasons.main }}

                        <ul class="reasons">
                            <li *ngFor="let reason of wordings.notProcessableReasons.reasons">
                                {{ reason }}
                            </li>
                        </ul>
                    </notification>

                    <bookings-list #bookingsList
                                   [canFilter]="false"
                                   [canExportExcel]="false"
                                   [selectable]="false"
                                   [selectedOptions]="bookingsList.selectedOptions"
                                   [bookings]="bookingListNotProcessable.bookings"></bookings-list>
                </mat-tab>
            </mat-tab-group>
        </div>
    `,
    styles: [`
        ul.reasons {
            margin-bottom: 0;
        }

        mat-tab-group ::ng-deep mat-tab-body {
            background-color: #fff;
        }
    `]
})
export class ProcessingReassuranceComponent implements OnInit {
    @ViewChild('bookingsList')
    public bookingsList: BookingsListComponent;

    @Input()
    public wordings: IProcessingReassuranceLabels;

    @Input("processableBookings")
    set processableBookings(processable: IGetBookingsCommonResponse[]) {
        const processableLabel: string = processable.length > 1 ?
            this.wordings.processableBookings :
            this.wordings.processableBooking;

        this.bookingListProcessable = {
            label: `${processable.length} ${processableLabel}`,
            bookings: processable
        };
    }

    @Input("notProcessableBookings")
    set notProcessableBookings(notProcessable: IGetBookingsCommonResponse[]) {
        const notProcessableLabel: string = notProcessable.length > 1 ?
            this.wordings.notProcessableBookings :
            this.wordings.notProcessableBooking;

        this.bookingListNotProcessable = {
            label: `${notProcessable.length} ${notProcessableLabel}`,
            bookings: notProcessable
        };
    }

    @Input()
    public set loading(value: boolean) {
        this.loader.active = value;
    }

    @Input()
    public set errorMsg(value: string) {
        this.notification = value ? {
            active: true,
            type: NotificationType.Error,
            message: value
        } : { active: false };
    }

    @Output()
    public readonly onProcessing: EventEmitter<IGetBookingsCommonResponse[]> = new EventEmitter();

    public bookingListProcessable: IBookingList;
    public bookingListNotProcessable: IBookingList;
    public notification: INotification = { active: false };
    public confirmDialogComponent: ConfirmDialogComponent = new ConfirmDialogComponent(null, null);
    public loader: ILoader = {
        active: false,
    };

    constructor(public dialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.notification = { active: false };

        // show notification if not processable bookings
        if (!this.bookingListProcessable.bookings.length) {
            this.notification = {
                type: NotificationType.Info,
                message: this.wordings.processableBookingsIsEmpty,
                active: true
            };
        }
    }

    public launchAction(selectedBookings: IGetBookingsCommonResponse[]): void {
        if (selectedBookings.length) {
            const confirmDialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
                width: '450px',
                data: this.wordings.dialog,
            });

            confirmDialog.afterClosed().pipe(
                filter((value: string) => value === 'process'),
                tap(() => {
                    this.notification.active = false;
                    this.loader.message = this.wordings.loaderMessage;
                }),
            ).subscribe(() => {
                this.onProcessing.emit(selectedBookings);
            });
        }
    }
}
